import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

interface ManifestsFiltersState {
  status: string;
  courier_integration_id: string;
  created_at: DateRange | undefined;
}

interface UseManifestsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useManifestsFilters = ({ initialConditions = [], onChange }: UseManifestsFiltersProps) => {
  const [filters, setFilters] = useState<ManifestsFiltersState>(() => parseConditionsToState(initialConditions));

  const convertStateToConditions = useCallback((state: ManifestsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status) {
      conditions.push({
        column: 'status',
        action: 'equals',
        value: state.status,
      });
    }

    if (state.courier_integration_id) {
      conditions.push({
        column: 'courier_integration_id',
        action: 'equals',
        value: state.courier_integration_id,
      });
    }

    // useManifestsMain espera created_at con action 'equals' y value "from to to"
    if (state.created_at?.from && state.created_at?.to) {
      conditions.push({
        column: 'created_at',
        action: 'equals',
        value: `${formatDateForAPI(state.created_at.from)} to ${formatDateForAPI(state.created_at.to)}`,
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<ManifestsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleStatusChange = useCallback((value: string) => updateFilters({ status: value }), [updateFilters]);
  const handleCourierChange = useCallback(
    (value: string) => updateFilters({ courier_integration_id: value }),
    [updateFilters],
  );
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ created_at: range }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: ManifestsFiltersState = {
      status: '',
      courier_integration_id: '',
      created_at: undefined,
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleStatusChange,
    handleCourierChange,
    handleCreatedAtChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): ManifestsFiltersState {
  const state: ManifestsFiltersState = {
    status: '',
    courier_integration_id: '',
    created_at: undefined,
  };

  for (const c of conditions) {
    if (c.column === 'status' && c.action === 'equals') {
      state.status = String(c.value);
    }
    if (c.column === 'courier_integration_id' && c.action === 'equals') {
      state.courier_integration_id = String(c.value);
    }
    if (c.column === 'created_at' && c.action === 'equals' && typeof c.value === 'string' && c.value.includes(' to ')) {
      const [fromStr, toStr] = c.value.split(' to ');
      if (fromStr && toStr) {
        state.created_at = {
          from: new Date(fromStr.trim()),
          to: new Date(toStr.trim()),
        };
      }
    }
  }

  return state;
}
